import { Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
  MoneyFlowType,
  moneyFlowTypeList,
  PropertyManagement,
  PropertyTransaction,
  WalletTransaction,
  walletTransactionList,
} from './property-management.model';
import { Subscription } from 'rxjs';
import { PropertyManagementService } from './property-management.service';
import { NgProgressService } from '@shared/services/ng-progress.service';
import { DocumentsService } from '@shared/services/documents.service';
import { ProfileService } from '../profile/profile.service';
import { CommonModule, DatePipe } from '@angular/common';
import {
  Document,
  mimeStringToFileTypeMap,
} from '../properties/properties.model';
import { PortfolioGrowthChartComponent } from '../../../shared/components/portfolio-growth-chart/portfolio-growth-chart.component';
import { PopoverModule } from 'primeng/popover';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { CardModule } from 'primeng/card';
import { SkeletonModule } from 'primeng/skeleton';
import { CustomCurrencyPipe } from '@shared/pipes/custom-currency.pipe';
import { TableModule } from 'primeng/table';
import { DividerModule } from 'primeng/divider';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { FluidModule } from 'primeng/fluid';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { InputMaskModule } from 'primeng/inputmask';
import { InputNumberModule } from 'primeng/inputnumber';
import { SelectModule } from 'primeng/select';
import { FileUpload, FileUploadModule } from 'primeng/fileupload';
import { RadioButtonModule } from 'primeng/radiobutton';
import { InputTextModule } from 'primeng/inputtext';
import { DatePickerModule } from 'primeng/datepicker';
import { TablerIconComponent } from 'angular-tabler-icons';
import { Router } from '@angular/router';

@Component({
  selector: 'app-property-management',
  imports: [
    CommonModule,
    PortfolioGrowthChartComponent,
    PopoverModule,
    AngularSvgIconModule,
    CardModule,
    SkeletonModule,
    CustomCurrencyPipe,
    TableModule,
    DividerModule,
    ButtonModule,
    DialogModule,
    FluidModule,
    InputMaskModule,
    FormsModule,
    ReactiveFormsModule,
    InputNumberModule,
    InputMaskModule,
    SelectModule,
    FileUploadModule,
    InputTextModule,
    RadioButtonModule,
    DatePickerModule,
    TablerIconComponent,
  ],
  templateUrl: './property-management.component.html',
  styleUrl: './property-management.component.css',
})
export class PropertyManagementComponent implements OnInit, OnDestroy {
  @ViewChild('fileUploader') fileUploader!: FileUpload;

  currency: string | undefined;
  propertyManagement: PropertyManagement | null = null;
  growthOverviewChartDates: string[] = [];
  isTouchDevice: boolean = false;
  WalletTransaction = WalletTransaction;
  MoneyFlowType = MoneyFlowType;
  walletTransactionList = walletTransactionList;
  moneyFlowTypeList = moneyFlowTypeList;

  showAddDocumentDialog: boolean = false;
  readonly addDocumentForm: FormGroup;

  showAddTransactionfialog: boolean = false;
  readonly addTransactionForm: FormGroup;

  private propertyManagementSub = Subscription.EMPTY;

  readonly _propertyManagementService = inject(PropertyManagementService);
  private readonly _ngProgressService = inject(NgProgressService);
  private readonly _profileService = inject(ProfileService);
  private readonly _documentsService = inject(DocumentsService);
  private readonly _formBuilder = inject(FormBuilder);
  private readonly _rouster = inject(Router);

  get fDocuments() {
    return this.addDocumentForm.controls;
  }

  get fTransactions() {
    return this.addTransactionForm.controls;
  }

  constructor() {
    this.addDocumentForm = this._formBuilder.group({
      fileLabel: [undefined, Validators.required],
      fileDocument: [undefined, Validators.required],
    });

    this.addTransactionForm = this._formBuilder.group({
      type: [undefined, Validators.required],
      moneyFlow: [undefined, Validators.required],
      date: [undefined, Validators.required],
      amount: [undefined, Validators.required],
    });
  }

  ngOnInit(): void {
    this.isTouchDevice = matchMedia('(pointer: coarse)').matches;
    this._ngProgressService.startLoader();

    this.currency = this._profileService.profile.currency;

    this.propertyManagementSub =
      this._propertyManagementService.propertyManagement$.subscribe(
        (propertyManagement) => {
          this.propertyManagement = propertyManagement;

          this.parseGrowthOverviewChartDates(
            this.propertyManagement?.growthOverviewChartDates
          );

          this._ngProgressService.completeLoader();
        }
      );

    this._propertyManagementService.getPropertyManagement();
  }

  onDownloadDocument(document: Document, event: Event): void {
    event.preventDefault(); // Prevents navigation
    event.stopPropagation(); // Stops event from bubbling to <a>

    const documentUrl = document.url;
    const documentName = `${document.name}.${document.type}`;

    this._documentsService.onDownloadDocument(documentUrl, documentName);
  }

  onRemoveProperty(index: number, event: Event) {
    event.preventDefault(); // Prevents navigation
    event.stopPropagation(); // Stops event from bubbling to <a>

    this.propertyManagement?.documents.splice(index, 1);
  }

  onShowAddDocumentDialog(): void {
    this.showAddDocumentDialog = true;
    this.addDocumentForm.reset();
    this.fileUploader.clear();
  }

  onAddDocument(): void {
    this.onUpload();
  }

  onShowAddTrasactionDialog(): void {
    this.showAddTransactionfialog = true;
    this.addTransactionForm.reset();
  }

  onAddTransaction(): void {
    if (this.addTransactionForm.invalid) {
      return;
    }

    this.showAddTransactionfialog = false;

    const transaction: PropertyTransaction = {
      type: this.fTransactions['type'].value.value,
      moneyFlow: this.fTransactions['moneyFlow'].value.value,
      date: this.fTransactions['date'].value,
      amount: this.fTransactions['amount'].value,
      isEditable: true,
    };

    this._propertyManagementService.addTransaction(transaction);
  }

  onUpload(): void {
    const file = this.fDocuments['fileDocument'].value;
    const label = this.fDocuments['fileLabel'].value;

    const mimeType = file.type; // e.g. 'application/pdf'
    const fileType = mimeStringToFileTypeMap[mimeType];

    const document: Document = {
      name: label,
      type: fileType,
      url: '',
      canBeDeleted: true,
    };

    this._propertyManagementService.addDocument(document);
    this.showAddDocumentDialog = false;
    this.addDocumentForm.reset();
    this.fileUploader.clear();

    // const label = this.fDocuments.fileLabel.value;

    // const formData = new FormData();
    // formData.append('file', file);
    // formData.append('label', label);

    // this._documentService.uploadFile(formData).subscribe(...);
  }

  onFileSelect(event: { files: File[] }): void {
    const file = event.files?.[0];

    if (file) {
      this.fDocuments['fileDocument'].setValue(file);
    } else {
      this.fDocuments['fileDocument'].setValue(null);
    }
  }

  onFileUploaded($event: any): void {}

  onNavigateToProperty(id: string | undefined): void {
    if (!id) {
      return;
    }

    this._rouster.navigate(['home/properties/view', id]);
  }

  private parseGrowthOverviewChartDates(
    growthOverviewChartDates: Date[] | undefined
  ): void {
    if (!growthOverviewChartDates) {
      this.growthOverviewChartDates = [];
      return;
    }

    const datePipe = new DatePipe('en-US');
    this.growthOverviewChartDates = growthOverviewChartDates!.map(
      (date) => datePipe.transform(date, 'd MMM y')!
    );
  }

  ngOnDestroy(): void {
    this.propertyManagementSub.unsubscribe();
  }
}
